import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { waitForAllReturnWhenError } from '@confluence/query-preloader-tools/entry-points/waitForAllReturnWhenError.ts';
import { themeSSRManager } from '@confluence/ssr-utilities/entry-points/ThemeSSRManager.ts';
import { getEffectiveThemeState } from '@confluence/theming-utils/entry-points/getEffectiveTheme.ts';
import { expValEquals } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';

import type {
	UserSettingsQuery as UserSettingsQueryType,
	UserSettingsQueryVariables as UserSettingsQueryVariablesType,
} from './__types__/UserSettingsQuery.ts';
import UserSettingsQuery from './__generated__/UserSettingsQuery.graphql.ts';
import { preloadWorkspaceThemeForSSR } from './preloadWorkspaceThemeForSSR.ts';

export const API_DEFAULT_THEME_STATE = 'default';

export const preloadUserSettingsForSSR = async ({ themeState }: { themeState?: string }) => {
	const { isLicensed, isLoggedIn } = getPreloaderFnContext();

	if (!isLoggedIn) {
		return;
	}

	// Apply themeState immediately if provided via query param (always defined for embedded pages)
	if (themeState) {
		themeSSRManager.setThemeStateBasedOnSavedThemeState(
			getEffectiveThemeState(decodeURIComponent(themeState)),
		);
	}

	// Always fetch user settings
	// When shared-user-preferences flag is on, also fetch the workspace theme from AGG in parallel
	const userSettingsPromise = query<UserSettingsQueryType, UserSettingsQueryVariablesType>({
		query: UserSettingsQuery,
		variables: {
			isNotLicensed: !isLicensed,
		},
	});

	const sharedThemePromise = fg('shared-user-preferences')
		? preloadWorkspaceThemeForSSR()
		: Promise.resolve(null);

	await waitForAllReturnWhenError([userSettingsPromise, sharedThemePromise]);

	const userSettingsResponse = await userSettingsPromise;
	const workspaceTheme = await sharedThemePromise;

	const response = userSettingsResponse;
	const legacyTheme = response?.data?.userPreferences?.theme;

	let theme: string | null | undefined;
	if (fg('shared-user-preferences')) {
		// Empty theme returned from API means there is no preference set, which should be treated as default (Auto color mode)
		theme = workspaceTheme === '' ? API_DEFAULT_THEME_STATE : workspaceTheme;
	} else {
		theme = legacyTheme;
	}

	// Apply user preference theme only when no themeState is provided (prevents flicker from mismatch)
	if (expValEquals('confluence_preview_panels_exp', 'isEnabled', true)) {
		if (!themeState && theme) {
			themeSSRManager.setThemeStateBasedOnSavedThemeState(getEffectiveThemeState(theme));
		}
	} else {
		if (theme) {
			themeSSRManager.setThemeStateBasedOnSavedThemeState(getEffectiveThemeState(theme));
		}
	}

	return response;
};
